// Static brokerage pages: About, Contact, FAQ, Testimonials, Why Choose Us,
// Mortgage Calculator, City Guides, Blog, Legal (Privacy, Terms, Fair Housing, IABS).
import React, { useState, useMemo } from "react";
import axios from "axios";
import { AGENT, BROKERAGE, BRAND, TREC } from "@/content/brand";
import { TESTIMONIALS } from "@/content/testimonials";
import { CITIES, cityBySlug } from "@/content/cities";
import { FAQ_GROUPS } from "@/content/faq";
import { BLOG_POSTS, postBySlug } from "@/content/blog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Section({ children, className = "" }) {
  return <section className={`mx-auto max-w-6xl px-4 py-10 md:px-8 md:py-14 ${className}`}>{children}</section>;
}

// ---------- SMS/TCPA consent (shared across every form that collects phone) ----------
// Text is intentionally verbose — Twilio A2P 10DLC review reads this verbatim.
export const SMS_CONSENT_TEXT =
  "By checking this box, I agree to receive SMS text messages from Irving Real Estate Group " +
  "(including transactional messages about my inquiry, appointment reminders, listing alerts, " +
  "and follow-up marketing) at the phone number I provided. Message frequency varies. " +
  "Message and data rates may apply. Reply STOP to unsubscribe or HELP for help at any time. " +
  "Consent is not a condition of any purchase or service. See our SMS Terms and Privacy Policy for details.";

export function SmsConsentCheckbox({ checked, onChange, theme = "light", navigate }) {
  const dark = theme === "dark";
  const boxCls = dark
    ? "mt-3 rounded-2xl border border-[#C4A56B]/30 bg-[#0F0F0F] p-4 text-xs text-[#E0D2A8]/85"
    : "mt-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-700";
  const linkCls = dark ? "font-semibold text-[#C4A56B] underline" : "font-semibold text-[#1E1E1E] underline";
  return (
    <label className={boxCls} data-testid="sms-consent-block">
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={!!checked}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-1 h-4 w-4 shrink-0 accent-[#C4A56B]"
          data-testid="sms-consent-checkbox"
        />
        <span className="leading-5">
          {SMS_CONSENT_TEXT}{" "}
          {navigate && (
            <>
              <button type="button" onClick={() => navigate("sms-terms")} className={linkCls}>SMS Terms</button>
              {" · "}
              <button type="button" onClick={() => navigate("privacy")} className={linkCls}>Privacy Policy</button>
            </>
          )}
        </span>
      </div>
    </label>
  );
}


function Eyebrow({ children }) {
  return <div className="text-sm font-semibold uppercase tracking-[0.18em] text-[#C4A56B]">{children}</div>;
}

function Stars({ count = 5 }) {
  return <div className="text-[#C4A56B]">{"★".repeat(count)}</div>;
}

function PageHero({ eyebrow, title, sub }) {
  return (
    <section className="bg-[#1E1E1E] text-white">
      <div className="mx-auto max-w-6xl px-4 py-12 md:px-8 md:py-16">
        {eyebrow && <div className="text-sm font-semibold uppercase tracking-[0.18em] text-[#C4A56B]">{eyebrow}</div>}
        <h1 className="mt-2 text-4xl font-bold leading-tight md:text-5xl">{title}</h1>
        {sub && <p className="mt-4 max-w-3xl text-lg text-slate-200">{sub}</p>}
      </div>
    </section>
  );
}

// ---------- About / Meet Torey ----------
export function AboutPage({ setView, startFlow }) {
  return (
    <main data-testid="about-view">
      <PageHero eyebrow="Meet your agent" title={`Meet ${AGENT.name}`} sub={AGENT.yearsLicensed} />
      <Section>
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="space-y-4">
            <div className="overflow-hidden rounded-[2rem] shadow-xl ring-1 ring-[#C4A56B]/20">
              <img src={AGENT.photo} alt={AGENT.name} className="w-full object-cover object-top" />
            </div>
            <div className="rounded-2xl border border-[#C4A56B]/25 bg-[#141414] p-5 shadow-lg">
              <div className="font-serif text-[#C4A56B]">{AGENT.name}</div>
              <div className="text-sm text-[#E0D2A8]/80">{AGENT.title}</div>
              <div className="mt-2 text-xs text-[#E0D2A8]/60">TX Realtor License #{AGENT.licenseNumber}</div>
              <div className="text-xs text-[#E0D2A8]/60">{AGENT.cityResidence}</div>
              <div className="mt-3 flex gap-2">
                <a data-testid="about-call-btn" href={`tel:${AGENT.phone}`} className="rounded-xl bg-[#C4A56B] px-4 py-2 text-sm font-bold text-[#1E1E1E]">Call {AGENT.phone}</a>
                <button data-testid="about-contact-btn" onClick={() => setView("contact")} className="rounded-xl border border-[#C4A56B]/40 px-4 py-2 text-sm font-bold text-[#E0D2A8]">Email Torey</button>
              </div>
            </div>
          </div>
          <div>
            <Eyebrow>His Story</Eyebrow>
            <div className="mt-3 space-y-4 text-lg leading-8 text-[#E0D2A8]/85">
              {AGENT.bio.split("\n\n").map((para) => <p key={para.slice(0, 32)}>{para}</p>)}
            </div>
            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-[#C4A56B]/25 bg-[#141414] p-4"><div className="font-serif text-3xl text-[#C4A56B]">15+</div><div className="text-xs uppercase tracking-wide text-[#E0D2A8]/70">Years in real estate finance</div></div>
              <div className="rounded-2xl border border-[#C4A56B]/25 bg-[#141414] p-4"><div className="font-serif text-3xl text-[#C4A56B]">2008</div><div className="text-xs uppercase tracking-wide text-[#E0D2A8]/70">Licensed Realtor since</div></div>
              <div className="rounded-2xl border border-[#C4A56B]/25 bg-[#141414] p-4"><div className="font-serif text-3xl text-[#C4A56B]">5.0★</div><div className="text-xs uppercase tracking-wide text-[#E0D2A8]/70">Client review average</div></div>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <button data-testid="about-buy-btn" onClick={() => startFlow("Buyer")} className="rounded-2xl bg-[#C4A56B] px-5 py-3 font-bold text-[#1E1E1E]">Work with Torey to Buy</button>
              <button data-testid="about-sell-btn" onClick={() => startFlow("Seller")} className="rounded-2xl border border-[#C4A56B]/40 px-5 py-3 font-bold text-[#E0D2A8]">Work with Torey to Sell</button>
            </div>
          </div>
        </div>
      </Section>
    </main>
  );
}

// ---------- Contact ----------
export function ContactPage({ navigate }) {
  const [sent, setSent] = useState(false);
  const [data, setData] = useState({ name: "", email: "", phone: "", message: "", smsConsent: false });
  function set(k, v) { setData((p) => ({ ...p, [k]: v })); }
  async function submit() {
    // Persist to CRM (so consent is recorded) then open the mail client.
    try {
      await axios.post(`${API}/leads`, {
        type: "Buyer",
        name: data.name,
        email: data.email,
        phone: data.phone,
        concern: data.message,
        source: "Contact Form",
        smsConsent: !!data.smsConsent,
        smsConsentText: data.smsConsent ? SMS_CONSENT_TEXT : "",
        smsConsentSource: "contact_form",
        hp: "",
      });
    } catch { /* fall through — mailto still opens */ }
    const subject = encodeURIComponent(`Website inquiry from ${data.name || "a visitor"}`);
    const body = encodeURIComponent(`${data.message}\n\n---\nFrom: ${data.name}\nEmail: ${data.email}\nPhone: ${data.phone}\nSMS opt-in: ${data.smsConsent ? "YES" : "NO"}`);
    window.location.href = `mailto:${BROKERAGE.email}?subject=${subject}&body=${body}`;
    setSent(true);
  }
  return (
    <main data-testid="contact-view">
      <PageHero eyebrow="Get in touch" title="Let's talk about your next move" sub="Phone, email, or come by the office in downtown Fort Worth." />
      <Section>
        <div className="grid gap-10 lg:grid-cols-2">
          <div className="space-y-4">
            <div className="rounded-3xl bg-white p-6 shadow-sm">
              <div className="font-bold text-lg">Office</div>
              <div className="mt-2 text-slate-700">{BROKERAGE.office.full}</div>
              <div className="mt-1 text-xs text-slate-500">{BROKERAGE.hours}</div>
            </div>
            <div className="rounded-3xl bg-white p-6 shadow-sm">
              <div className="font-bold text-lg">Phone</div>
              <a href={`tel:${BROKERAGE.phone}`} className="mt-2 inline-block text-xl font-bold text-[#1E1E1E]">{BROKERAGE.phone}</a>
            </div>
            <div className="rounded-3xl bg-white p-6 shadow-sm">
              <div className="font-bold text-lg">Email</div>
              <a href={`mailto:${BROKERAGE.email}`} className="mt-2 inline-block text-slate-700 underline">{BROKERAGE.email}</a>
            </div>
            <div className="overflow-hidden rounded-3xl shadow-sm">
              <iframe
                title="Office map"
                width="100%"
                height="280"
                loading="lazy"
                style={{ border: 0 }}
                src={`https://maps.google.com/maps?q=${encodeURIComponent(BROKERAGE.office.full)}&output=embed`}
              />
            </div>
          </div>
          <div className="rounded-[2rem] bg-white p-6 shadow-sm md:p-8">
            <div className="text-sm font-semibold uppercase tracking-[0.18em] text-[#1E1E1E]">Send a message</div>
            <h2 className="mt-2 text-2xl font-bold">We typically respond within 1 business hour.</h2>
            {sent ? (
              <div data-testid="contact-success" className="mt-6 rounded-2xl bg-[#f6f2ea] p-5">
                <div className="text-3xl">✉️</div>
                <div className="mt-2 font-bold">Your email client just opened.</div>
                <div className="mt-1 text-sm text-slate-600">If nothing happened, you can email {BROKERAGE.email} directly.</div>
              </div>
            ) : (
              <div className="mt-4 grid gap-3">
                <input data-testid="contact-name" value={data.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" className="rounded-2xl border border-slate-300 px-4 py-3" />
                <input data-testid="contact-email" value={data.email} onChange={(e) => set("email", e.target.value)} placeholder="Email" type="email" className="rounded-2xl border border-slate-300 px-4 py-3" />
                <input data-testid="contact-phone" value={data.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone (optional)" className="rounded-2xl border border-slate-300 px-4 py-3" />
                <textarea data-testid="contact-message" value={data.message} onChange={(e) => set("message", e.target.value)} placeholder="How can Torey help?" className="min-h-32 rounded-2xl border border-slate-300 px-4 py-3" />
                <SmsConsentCheckbox checked={data.smsConsent} onChange={(v) => set("smsConsent", v)} navigate={navigate} />
                <button data-testid="contact-submit" disabled={!data.name || !data.email || !data.message} onClick={submit} className="rounded-2xl bg-[#1E1E1E] px-6 py-3 font-bold text-white disabled:opacity-50">Send message</button>
              </div>
            )}
          </div>
        </div>
      </Section>
    </main>
  );
}

// ---------- Testimonials ----------
export function TestimonialsSection({ compact = false, max }) {
  const items = max ? TESTIMONIALS.slice(0, max) : TESTIMONIALS;
  return (
    <div data-testid="testimonials-section" className={compact ? "" : "mx-auto max-w-6xl px-4 py-12 md:px-8"}>
      {!compact && (
        <div className="mb-8 text-center">
          <Eyebrow>What clients say</Eyebrow>
          <h2 className="mt-2 text-3xl font-bold">Real stories from real DFW buyers.</h2>
        </div>
      )}
      <div className="grid gap-6 md:grid-cols-3">
        {items.map((t) => (
          <div key={t.id} data-testid={`testimonial-${t.id}`} className="rounded-[2rem] border border-[#C4A56B]/25 bg-[#1a1a1a] p-6 shadow-lg">
            <Stars count={t.rating} />
            <p className="mt-4 text-sm leading-7 text-[#E0D2A8]/85">"{t.quote}"</p>
            <div className="mt-5 border-t border-[#C4A56B]/20 pt-4">
              <div className="font-serif text-[#C4A56B]">{t.name}</div>
              <div className="text-xs text-[#E0D2A8]/60">{t.transaction}</div>
              <div className="text-xs text-[#E0D2A8]/60">{t.location}</div>
              <div className="mt-1 text-xs text-[#E0D2A8]/40">{t.date}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function TestimonialsPage() {
  return (
    <main data-testid="testimonials-view">
      <PageHero eyebrow="Client reviews" title="What working with Torey is actually like." sub="Five-star reviews from real DFW buyers and sellers. no paid placements." />
      <Section><TestimonialsSection compact /></Section>
    </main>
  );
}

// ---------- Why Choose Us ----------
export function WhyUsPage({ setView }) {
  const items = [
    { t: "Live MLS data, not delayed feeds", d: "We pull directly from NTREIS so what you see is what's actually for sale today. not Zillow's 24-hour-old cache." },
    { t: "Jason, your 24/7 concierge", d: "Get answers about loans, neighborhoods, or your active transaction at 2am. Then Torey takes over when you want a human." },
    { t: "Transaction concierge from offer to close", d: "An 8-stage tracker with auto-checklists makes sure nothing slips. You always know what's next and what to do." },
    { t: "Built-in compliance", d: "Texas-licensed, fair-housing-trained, no shady steering. Jason refuses to quote rates or make qualifying decisions. only your licensed lender can." },
    { t: "DFW-specific expertise", d: "Torey lives in Fort Worth, has worked the DFW market since 2008, and personally negotiates every deal." },
    { t: "No pressure, ever", d: "We respect your timeline. You set the pace. Torey only follows up when you say you're ready." },
  ];
  return (
    <main data-testid="why-us-view">
      <PageHero eyebrow="Why choose us" title="The Irving Experience is different on purpose." sub="Six things you'll get with Irving Real Estate Group that you won't find with the average DFW realtor." />
      <Section>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {items.map((i) => (
            <div key={i.t} className="rounded-[2rem] bg-white p-6 shadow-sm">
              <div className="text-xl font-bold">{i.t}</div>
              <p className="mt-3 leading-7 text-slate-600">{i.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <button data-testid="why-us-cta" onClick={() => setView("contact")} className="rounded-2xl bg-[#C4A56B] px-6 py-4 font-bold text-[#1E1E1E] shadow-lg">Let's talk about your move</button>
        </div>
      </Section>
    </main>
  );
}

// ---------- FAQ ----------
export function FaqPage() {
  return (
    <main data-testid="faq-view">
      <PageHero eyebrow="FAQ" title="Real estate questions, honestly answered." sub="The questions buyers and sellers actually ask Torey on day one." />
      <Section>
        <div className="space-y-8">
          {FAQ_GROUPS.map((group) => (
            <div key={group.title}>
              <div className="text-2xl font-bold">{group.title}</div>
              <div className="mt-4 space-y-3">
                {group.items.map((item, i) => (
                  <details key={item.q} data-testid={`faq-item-${group.title.split(" ")[0].toLowerCase()}-${i}`} className="rounded-2xl bg-white p-5 shadow-sm">
                    <summary className="cursor-pointer font-semibold text-[#1E1E1E]">{item.q}</summary>
                    <p className="mt-3 leading-7 text-slate-700">{item.a}</p>
                  </details>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>
    </main>
  );
}

// ---------- Mortgage Calculator (pure math, no qualifying decision) ----------
// Sensible defaults for the median DFW first-time-buyer scenario.
const MORTGAGE_DEFAULT_PRICE = 400_000;
const MORTGAGE_DEFAULT_DOWN_PCT = 20;
const MORTGAGE_DEFAULT_RATE = 6.5;
const MORTGAGE_DEFAULT_TERM_YEARS = 30;
const MORTGAGE_DEFAULT_TAXES_YEARLY = 8_000;
const MORTGAGE_DEFAULT_INSURANCE_YEARLY = 2_400;
const MORTGAGE_TERM_OPTIONS = [15, 20, 30];
const MONTHS_PER_YEAR = 12;

export function MortgagePage() {
  const [price, setPrice] = useState(MORTGAGE_DEFAULT_PRICE);
  const [downPct, setDownPct] = useState(MORTGAGE_DEFAULT_DOWN_PCT);
  const [rate, setRate] = useState(MORTGAGE_DEFAULT_RATE);
  const [years, setYears] = useState(MORTGAGE_DEFAULT_TERM_YEARS);
  const [taxesYr, setTaxesYr] = useState(MORTGAGE_DEFAULT_TAXES_YEARLY);
  const [insuranceYr, setInsuranceYr] = useState(MORTGAGE_DEFAULT_INSURANCE_YEARLY);

  const result = useMemo(() => {
    const loan = price * (1 - downPct / 100);
    const monthlyRate = (rate / 100) / MONTHS_PER_YEAR;
    const numPayments = years * MONTHS_PER_YEAR;
    const pi = monthlyRate === 0
      ? loan / numPayments
      : (loan * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -numPayments));
    const monthly = pi + (taxesYr / MONTHS_PER_YEAR) + (insuranceYr / MONTHS_PER_YEAR);
    return { loan, pi, monthly };
  }, [price, downPct, rate, years, taxesYr, insuranceYr]);

  function fmt(v) { return `$${Math.round(v).toLocaleString()}`; }

  return (
    <main data-testid="mortgage-view">
      <PageHero eyebrow="Mortgage calculator" title="Estimate your monthly payment." sub="This is general math, not a Loan Estimate. Your actual rate and payment depend on credit, loan type, and lender. get pre-approved for real numbers." />
      <Section>
        <div className="grid gap-8 md:grid-cols-2">
          <div className="rounded-[2rem] bg-white p-6 shadow-sm md:p-8 space-y-5">
            <div>
              <label className="text-sm font-semibold">Home price · {fmt(price)}</label>
              <input data-testid="mortgage-price" type="range" min="100000" max="2000000" step="5000" value={price} onChange={(e) => setPrice(+e.target.value)} className="mt-2 w-full accent-[#C4A56B]" />
              <input type="number" value={price} onChange={(e) => setPrice(+e.target.value)} className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2" />
            </div>
            <div>
              <label className="text-sm font-semibold">Down payment · {downPct}% ({fmt(price * downPct / 100)})</label>
              <input data-testid="mortgage-down" type="range" min="0" max="50" step="1" value={downPct} onChange={(e) => setDownPct(+e.target.value)} className="mt-2 w-full accent-[#C4A56B]" />
            </div>
            <div>
              <label className="text-sm font-semibold">Interest rate · {rate}%</label>
              <input data-testid="mortgage-rate" type="range" min="3" max="10" step="0.125" value={rate} onChange={(e) => setRate(+e.target.value)} className="mt-2 w-full accent-[#C4A56B]" />
            </div>
            <div>
              <label className="text-sm font-semibold">Loan term · {years} years</label>
              <div className="mt-2 flex gap-2">
                {MORTGAGE_TERM_OPTIONS.map((y) => (
                  <button key={y} data-testid={`mortgage-term-${y}`} onClick={() => setYears(y)} className={`flex-1 rounded-xl px-3 py-2 text-sm font-bold ${years === y ? "bg-[#1E1E1E] text-white" : "bg-slate-100"}`}>{y} yr</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold">Taxes (yearly)</label>
                <input data-testid="mortgage-taxes" type="number" value={taxesYr} onChange={(e) => setTaxesYr(+e.target.value)} className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2" />
              </div>
              <div>
                <label className="text-sm font-semibold">Insurance (yearly)</label>
                <input data-testid="mortgage-insurance" type="number" value={insuranceYr} onChange={(e) => setInsuranceYr(+e.target.value)} className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2" />
              </div>
            </div>
          </div>
          <div className="rounded-[2rem] bg-[#1E1E1E] p-6 text-white shadow-sm md:p-8">
            <div className="text-sm text-[#C4A56B]">Estimated monthly</div>
            <div data-testid="mortgage-monthly" className="mt-2 text-6xl font-bold">{fmt(result.monthly)}</div>
            <div className="mt-1 text-sm text-slate-300">principal, interest, taxes &amp; insurance</div>
            <div className="mt-8 space-y-3 border-t border-white/20 pt-6 text-sm">
              <Row label="Loan amount" value={fmt(result.loan)} />
              <Row label="Principal &amp; interest" value={fmt(result.pi)} />
              <Row label="Property taxes" value={fmt(taxesYr / 12)} />
              <Row label="Homeowner's insurance" value={fmt(insuranceYr / 12)} />
            </div>
            <div className="mt-8 rounded-xl bg-white/10 p-4 text-xs leading-6">
              <strong className="text-[#C4A56B]">Not a Loan Estimate.</strong> This is illustrative only. Your actual rate and monthly payment will depend on your credit profile, loan type, lock period, and lender. Get pre-approved with a licensed lender for real numbers.
            </div>
          </div>
        </div>
      </Section>
    </main>
  );
}
function Row({ label, value }) {
  return <div className="flex items-center justify-between"><span className="text-slate-300">{label}</span><span className="font-bold">{value}</span></div>;
}

// ---------- City Guides ----------
export function CityIndexPage({ setView, navigateCity }) {
  return (
    <main data-testid="city-index-view">
      <PageHero eyebrow="DFW neighborhoods" title="Where do you want to live?" sub="Nine DFW cities, real numbers and on-the-ground neighborhood notes. written by a Fort Worth resident, not an algorithm." />
      <Section>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {CITIES.map((c) => (
            <button key={c.slug} data-testid={`city-card-${c.slug}`} onClick={() => navigateCity(c.slug)} className="overflow-hidden rounded-[1.5rem] bg-white text-left shadow-sm transition hover:shadow-lg">
              <img src={c.image} alt={c.name} className="h-44 w-full object-cover" loading="lazy" />
              <div className="p-5">
                <div className="text-xl font-bold">{c.name}</div>
                <p className="mt-2 text-sm leading-6 text-slate-600">{c.blurb}</p>
                <div className="mt-3 text-xs font-semibold text-[#1E1E1E]">Explore {c.name} →</div>
              </div>
            </button>
          ))}
        </div>
      </Section>
    </main>
  );
}

export function CityDetailPage({ slug, openListings, startFlow }) {
  const city = cityBySlug(slug);
  if (!city) return <main className="mx-auto max-w-3xl p-10 text-center">City not found.</main>;
  return (
    <main data-testid={`city-view-${city.slug}`}>
      <section className="bg-[#1E1E1E] text-white">
        <div className="mx-auto grid max-w-6xl gap-8 px-4 py-12 md:px-8 lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <Eyebrow>City guide</Eyebrow>
            <h1 className="mt-2 text-4xl font-bold md:text-5xl">{city.headline}</h1>
            <p className="mt-4 text-lg text-slate-200">{city.blurb}</p>
            <ul className="mt-6 grid gap-2 text-slate-200">
              {city.highlights.map((h) => <li key={h}>✓ {h}</li>)}
            </ul>
            <div className="mt-6 flex flex-wrap gap-2 text-xs">
              {city.neighborhoods.map((n) => <span key={n} className="rounded-full bg-white/10 px-3 py-1">{n}</span>)}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <button data-testid="city-see-listings" onClick={openListings} className="rounded-2xl bg-[#C4A56B] px-5 py-3 font-bold text-[#1E1E1E]">See {city.name} listings</button>
              <button data-testid="city-buy-flow" onClick={() => startFlow("Buyer")} className="rounded-2xl border border-[#C4A56B] px-5 py-3 font-bold text-[#C4A56B]">Get matched homes</button>
            </div>
          </div>
          <img src={city.image} alt={city.name} className="h-80 w-full rounded-[2rem] object-cover lg:h-full" />
        </div>
      </section>
    </main>
  );
}

// ---------- Blog ----------
export function BlogIndexPage({ navigatePost }) {
  return (
    <main data-testid="blog-index-view">
      <PageHero eyebrow="Market insights" title="Real estate, explained without the spin." sub="Honest, useful guides for DFW buyers and sellers. written by Torey, not a content farm." />
      <Section>
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {BLOG_POSTS.map((p) => (
            <button key={p.slug} data-testid={`blog-card-${p.slug}`} onClick={() => navigatePost(p.slug)} className="overflow-hidden rounded-[1.5rem] bg-white text-left shadow-sm transition hover:shadow-lg">
              <img src={p.image} alt={p.title} className="h-48 w-full object-cover" loading="lazy" />
              <div className="p-5">
                <div className="text-xs uppercase tracking-wide text-slate-500">{new Date(p.publishedAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })} · {p.readMinutes} min read</div>
                <div className="mt-2 text-xl font-bold leading-snug">{p.title}</div>
                <p className="mt-2 text-sm leading-6 text-slate-600">{p.excerpt}</p>
                <div className="mt-3 text-xs font-bold text-[#1E1E1E]">Read post →</div>
              </div>
            </button>
          ))}
        </div>
      </Section>
    </main>
  );
}

export function BlogPostPage({ slug, navigateBlog, startFlow }) {
  const post = postBySlug(slug);
  if (!post) return <main className="mx-auto max-w-3xl p-10 text-center">Post not found.</main>;
  return (
    <main data-testid={`blog-post-${post.slug}`}>
      <PageHero eyebrow={`${post.readMinutes} min read · ${new Date(post.publishedAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`} title={post.title} sub={post.excerpt} />
      <Section>
        <button onClick={navigateBlog} className="mb-6 rounded-full bg-white px-4 py-2 text-sm font-semibold shadow-sm">← All posts</button>
        <div className="grid gap-10 lg:grid-cols-[2fr_1fr]">
          <article className="prose prose-slate max-w-none rounded-[2rem] bg-white p-6 shadow-sm md:p-10">
            <img src={post.image} alt={post.title} className="mb-6 w-full rounded-2xl object-cover" />
            {post.body.split("\n\n").map((block) => {
              const blockKey = block.slice(0, 32);
              if (block.startsWith("## ")) return <h2 key={blockKey} className="mt-8 text-2xl font-bold">{block.slice(3)}</h2>;
              if (block.startsWith("### ")) return <h3 key={blockKey} className="mt-6 text-xl font-bold">{block.slice(4)}</h3>;
              if (block.startsWith("- ")) return <ul key={blockKey} className="my-3 list-disc pl-6">{block.split("\n").map((li) => <li key={li} className="my-1">{li.replace(/^- /, "")}</li>)}</ul>;
              return <p key={blockKey} className="my-3 leading-8 text-slate-700">{block}</p>;
            })}
            <div className="mt-8 text-sm text-slate-500">Written by <strong>{post.author}</strong></div>
          </article>
          <aside className="space-y-4">
            <div className="rounded-[2rem] bg-[#1E1E1E] p-6 text-white">
              <div className="text-sm text-[#C4A56B]">Ready to start?</div>
              <div className="mt-2 text-xl font-bold">Let's see what makes sense for your timeline.</div>
              <button onClick={() => startFlow("Buyer")} className="mt-4 w-full rounded-xl bg-[#C4A56B] px-4 py-3 text-sm font-bold text-[#1E1E1E]">I want to buy</button>
              <button onClick={() => startFlow("Seller")} className="mt-2 w-full rounded-xl border border-white/30 px-4 py-3 text-sm font-bold">I want to sell</button>
            </div>
          </aside>
        </div>
      </Section>
    </main>
  );
}

// ---------- Legal pages ----------
export function PrivacyPage() {
  return (
    <main data-testid="privacy-view">
      <PageHero eyebrow="Legal" title="Privacy Policy" />
      <Section>
        <div className="prose prose-slate max-w-none rounded-[2rem] bg-white p-6 shadow-sm md:p-10">
          <p>Last updated: January 15, 2026</p>
          <p>Irving Real Estate Group ("we", "us") respects your privacy. This policy explains what data we collect, why, and how we use it.</p>
          <h2>Information we collect</h2>
          <ul>
            <li><strong>Contact info you give us</strong>. name, phone, email, property address. when you fill out a form, sign in, or ask Jason a question.</li>
            <li><strong>Property activity</strong>. which listings you view and favorite (only after you've identified yourself).</li>
            <li><strong>Transaction details</strong>. when you go under contract with us, we store the property, dates, lender, and title company to manage your deal.</li>
            <li><strong>Marketing attribution</strong>. UTM tags, referring URL, Google ads click IDs (gclid).</li>
          </ul>
          <h2>How we use it</h2>
          <ul>
            <li>To respond to your inquiries.</li>
            <li>To recommend homes that match your stated criteria.</li>
            <li>To manage your active transaction from contract to close.</li>
            <li>To measure which marketing channels work (we don't sell your data).</li>
          </ul>
          <h2>Who we share it with</h2>
          <p>Only service providers we use to run the site (hosting, chat, email, SMS) and only as needed. We do not sell your information. If you go under contract, we share necessary details with your lender, title company, and inspector.</p>
          <h2>Your choices</h2>
          <p>Email {BROKERAGE.email} to access, correct, or delete your data. Reply STOP to any SMS to opt out, or visit our <a href="#unsubscribe">unsubscribe page</a>.</p>
          <h2>SMS / text messaging</h2>
          <p><strong>How you opt in.</strong> You give us permission to text you by checking the SMS consent box on any of our forms (Contact, Buyer Intake, Seller Intake, Home Value Request, or For Agents). Consent is <strong>never</strong> pre-checked and is <strong>not required</strong> to buy or sell a home with us.</p>
          <p><strong>What we send.</strong> Transactional messages about your inquiry, appointment reminders, listing alerts you asked for, and follow-up marketing from Irving Real Estate Group. Message frequency varies.</p>
          <p><strong>Costs.</strong> Message and data rates may apply based on your carrier plan.</p>
          <p><strong>How you opt out.</strong> Reply <strong>STOP</strong> to any SMS to unsubscribe immediately. Reply <strong>HELP</strong> for help. You can also submit an opt-out request on our <a href="#unsubscribe">unsubscribe page</a> or email {BROKERAGE.email}.</p>
          <p><strong>Data sharing.</strong> Mobile opt-in data and consent are <strong>not</strong> shared with third parties or affiliates for their marketing purposes. See our <a href="#sms-terms">SMS Terms</a> for the full policy.</p>
          <h2>Contact</h2>
          <p>Questions? Email {BROKERAGE.email} or call {BROKERAGE.phone}.</p>
        </div>
      </Section>
    </main>
  );
}

// ---------- SMS Terms of Service (Twilio A2P 10DLC required) ----------
export function SmsTermsPage() {
  return (
    <main data-testid="sms-terms-view">
      <PageHero eyebrow="Legal" title="SMS Terms of Service" />
      <Section>
        <div className="prose prose-slate max-w-none rounded-[2rem] bg-white p-6 shadow-sm md:p-10">
          <p>Last updated: February 15, 2026</p>
          <p>These SMS Terms describe how Irving Real Estate Group LLC ("we", "us") sends text messages to you, how to opt in, and how to stop them. They apply in addition to our <a href="/privacy">Privacy Policy</a> and <a href="/terms">Terms of Service</a>.</p>
          <h2>1. Program description</h2>
          <p>Our SMS program sends real-estate-related messages to consumers who have given us prior express consent. Message categories include:</p>
          <ul>
            <li><strong>Transactional</strong>: replies to your inquiry, appointment confirmations, showing reminders, transaction milestone updates (option period, closing, funding).</li>
            <li><strong>Listing alerts</strong>: new-listing notifications matching criteria you requested.</li>
            <li><strong>Marketing follow-up</strong>: seller/buyer nurture, market updates, and event invitations.</li>
          </ul>
          <h2>2. How to opt in</h2>
          <p>You opt in only by checking the SMS consent checkbox on one of our website forms and submitting your phone number. The exact consent language you agree to is:</p>
          <blockquote>By checking this box, I agree to receive SMS text messages from Irving Real Estate Group (including transactional messages about my inquiry, appointment reminders, listing alerts, and follow-up marketing) at the phone number I provided. Message frequency varies. Message and data rates may apply. Reply STOP to unsubscribe or HELP for help at any time. Consent is not a condition of any purchase or service.</blockquote>
          <p>The checkbox is <strong>never pre-checked</strong>. Consent to receive SMS is <strong>not</strong> a condition of buying, selling, or listing a home with us.</p>
          <h2>3. Message frequency</h2>
          <p>Message frequency varies based on the stage of your inquiry. Typical volume: 2-6 messages per month for prospective clients, up to 3 messages per week during an active transaction.</p>
          <h2>4. Message and data rates</h2>
          <p>Message and data rates may apply based on your carrier plan. We do not charge you for messages, but your carrier may.</p>
          <h2>5. How to opt out (STOP)</h2>
          <p>You can opt out at any time by replying <strong>STOP</strong> to any message we send. You will receive a single confirmation message and then no further texts from us. You can also opt out by:</p>
          <ul>
            <li>Submitting your number on our <a href="#unsubscribe">SMS unsubscribe page</a>.</li>
            <li>Emailing <a href={`mailto:${BROKERAGE.email}`}>{BROKERAGE.email}</a> with "STOP SMS" in the subject line.</li>
            <li>Calling us at <a href={`tel:${BROKERAGE.phone}`}>{BROKERAGE.phone}</a>.</li>
          </ul>
          <h2>6. How to get help (HELP)</h2>
          <p>Reply <strong>HELP</strong> to any message to receive a message with our support contact info, or email <a href={`mailto:${BROKERAGE.email}`}>{BROKERAGE.email}</a>.</p>
          <h2>7. Supported carriers</h2>
          <p>Compatible with major U.S. carriers including AT&amp;T, Verizon, T-Mobile, Sprint, and most regional MVNOs. Carriers are not liable for delayed or undelivered messages.</p>
          <h2>8. Data sharing (no resale)</h2>
          <p><strong>We do not share, sell, or rent your phone number, opt-in data, or consent record to third parties or affiliates for their own marketing purposes.</strong> Data is only used to deliver the messages you opted in to receive. We use Twilio, Inc. as our messaging service provider — Twilio processes messages on our behalf and is bound by their own privacy commitments.</p>
          <h2>9. Legal</h2>
          <p>Irving Real Estate Group LLC is a Texas limited liability company (EIN 84-3245581), registered at 600 W 6th St, Fort Worth TX 76102. TX Broker License #{BROKERAGE.licenseNumber}.</p>
          <p>Questions about these SMS Terms? Email <a href={`mailto:${BROKERAGE.email}`}>{BROKERAGE.email}</a>.</p>
        </div>
      </Section>
    </main>
  );
}

// ---------- Unsubscribe page (public opt-out form) ----------
export function UnsubscribePage() {
  const [phone, setPhone] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    setStatus("sending"); setErr("");
    try {
      await axios.post(`${API}/sms/opt-out`, { phone, source: "unsubscribe_page" });
      setStatus("done");
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Something went wrong. Try again or email us.");
      setStatus("error");
    }
  }

  return (
    <main data-testid="unsubscribe-view">
      <PageHero eyebrow="SMS" title="Unsubscribe from text messages" sub="Enter the phone number you want removed. Works even if you never subscribed." />
      <Section>
        <div className="mx-auto max-w-xl rounded-[2rem] bg-white p-6 shadow-sm md:p-10">
          {status === "done" ? (
            <div data-testid="unsubscribe-success" className="rounded-2xl bg-[#f6f2ea] p-6">
              <div className="text-3xl">✓</div>
              <div className="mt-2 text-lg font-bold">You're unsubscribed.</div>
              <p className="mt-2 text-sm text-slate-600">
                We won't send any more SMS to <strong>{phone}</strong>. This takes effect immediately. If you get a text
                from us after today, forward it to <a href={`mailto:${BROKERAGE.email}`}>{BROKERAGE.email}</a> and we'll fix it.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <label className="block text-sm">
                <span className="mb-1 block font-semibold text-slate-700">Phone number</span>
                <input
                  data-testid="unsubscribe-phone"
                  required
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(817) 555-1234"
                  className="w-full rounded-2xl border border-slate-300 px-4 py-3"
                />
              </label>
              {err && <div data-testid="unsubscribe-error" className="rounded-xl border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
              <button
                data-testid="unsubscribe-submit"
                type="submit"
                disabled={status === "sending" || !phone}
                className="w-full rounded-2xl bg-[#1E1E1E] px-6 py-3 font-bold text-white disabled:opacity-50"
              >
                {status === "sending" ? "Unsubscribing…" : "Unsubscribe me from SMS"}
              </button>
              <p className="text-center text-xs text-slate-500">
                You can also reply <strong>STOP</strong> to any text we send. Questions?{" "}
                <a href={`mailto:${BROKERAGE.email}`} className="underline">{BROKERAGE.email}</a>
              </p>
            </form>
          )}
        </div>
      </Section>
    </main>
  );
}

export function TermsPage() {
  return (
    <main data-testid="terms-view">
      <PageHero eyebrow="Legal" title="Terms of Service" />
      <Section>
        <div className="prose prose-slate max-w-none rounded-[2rem] bg-white p-6 shadow-sm md:p-10">
          <p>Last updated: February 15, 2026</p>
          <p>By using {BRAND.domain}, you agree to these terms.</p>
          <h2>Use of the site</h2>
          <p>This site is for your personal, non-commercial use. You may not scrape, resell, or republish listing data. NTREIS data is licensed for consumer browsing only.</p>
          <h2>No professional advice</h2>
          <p>Jason and the content on this site provide <strong>general education only</strong>. Nothing here is legal, tax, lending, appraisal, or investment advice. For specific decisions, consult a licensed professional. Loan rates, payments, and qualification require a licensed lender.</p>
          <h2>Fair housing</h2>
          <p>Irving Real Estate Group is an Equal Housing Opportunity provider. We comply with the Federal Fair Housing Act and Texas state law. We do not discriminate based on race, color, religion, sex, national origin, familial status, disability, or any protected characteristic.</p>
          <h2>Listing accuracy</h2>
          <p>We work hard to keep listings accurate but cannot guarantee real-time correctness. Always verify with your agent before making decisions.</p>
          <h2>SMS / text messaging</h2>
          <p>By opting in on any of our forms, you consent to receive SMS text messages from Irving Real Estate Group at the phone number you provided. Message frequency varies. Message and data rates may apply. Consent is not a condition of any purchase or service. You may opt out at any time by replying STOP or visiting our <a href="#unsubscribe">unsubscribe page</a>. See our full <a href="#sms-terms">SMS Terms</a> for details.</p>
          <h2>Limitation of liability</h2>
          <p>We are not liable for indirect or consequential damages arising from your use of the site. Maximum liability is limited to fees actually paid to us.</p>
        </div>
      </Section>
    </main>
  );
}

export function FairHousingPage() {
  return (
    <main data-testid="fair-housing-view">
      <PageHero eyebrow="Compliance" title="Fair Housing & Equal Opportunity" />
      <Section>
        <div className="prose prose-slate max-w-none rounded-[2rem] bg-white p-6 shadow-sm md:p-10">
          <p><strong>Irving Real Estate Group is an Equal Housing Opportunity provider.</strong></p>
          <p>It is illegal to discriminate against any person because of race, color, religion, sex (including gender identity and sexual orientation), handicap, familial status, or national origin in the sale or rental of housing.</p>
          <h2>What this means in practice</h2>
          <ul>
            <li>We do not steer buyers toward or away from neighborhoods based on demographic characteristics.</li>
            <li>We do not refuse to show listings based on protected characteristics.</li>
            <li>Our assistant Jason is explicitly trained to refuse any request that would violate fair housing rules.</li>
            <li>We treat every client with the same standard of service.</li>
          </ul>
          <h2>Report a fair housing concern</h2>
          <p>If you believe you've experienced housing discrimination, file a complaint with HUD at <a href="https://www.hud.gov/program_offices/fair_housing_equal_opp" target="_blank" rel="noopener noreferrer">hud.gov/fairhousing</a> or call 1-800-669-9777.</p>
          <p>Texas residents can also contact the Texas Workforce Commission Civil Rights Division at 1-888-452-4778.</p>
        </div>
      </Section>
    </main>
  );
}

export function IabsPage() {
  return (
    <main data-testid="iabs-view">
      <PageHero eyebrow="TREC required disclosure" title="Information About Brokerage Services" sub="Texas law requires all real estate license holders to give the following information about brokerage services to prospective buyers, tenants, sellers, and landlords." />
      <Section>
        <div className="prose prose-slate max-w-none rounded-[2rem] bg-white p-6 shadow-sm md:p-10">
          <h2>Types of Real Estate License Holders</h2>
          <p><strong>BROKER:</strong> A broker is responsible for all brokerage activities, including acts performed by sales agents sponsored by the broker.</p>
          <p><strong>SALES AGENT:</strong> A sales agent must be sponsored by a broker and works with clients on behalf of the broker.</p>

          <h2>A Broker's Minimum Duties Required by Law</h2>
          <p>A broker who represents you owes you the following:</p>
          <ul>
            <li>Put your interests above their own, including the broker's own interests</li>
            <li>Inform you of any material information about the property or transaction received by the broker</li>
            <li>Answer your questions and present any offer to or counter-offer from you</li>
            <li>Treat all parties to a real estate transaction honestly and fairly</li>
          </ul>

          <h2>License Holder Contact Information</h2>
          <div className="not-prose mt-4 grid gap-4 rounded-2xl bg-[#f6f2ea] p-5 md:grid-cols-2">
            <div>
              <div className="text-xs font-bold uppercase text-slate-500">Designated Broker / Firm</div>
              <div className="mt-1 font-semibold">{BROKERAGE.name}</div>
              <div className="text-sm text-slate-600">License #{BROKERAGE.licenseNumber}</div>
              <div className="text-sm text-slate-600">{BROKERAGE.office.full}</div>
              <div className="text-sm text-slate-600">{BROKERAGE.phone}</div>
              <div className="text-sm text-slate-600">{BROKERAGE.email}</div>
            </div>
            <div>
              <div className="text-xs font-bold uppercase text-slate-500">Sales Agent / Realtor</div>
              <div className="mt-1 font-semibold">{AGENT.name}</div>
              <div className="text-sm text-slate-600">License #{AGENT.licenseNumber}</div>
              <div className="text-sm text-slate-600">{AGENT.phone}</div>
              <div className="text-sm text-slate-600">{AGENT.email}</div>
            </div>
          </div>

          <p className="mt-6">Regulated by the Texas Real Estate Commission · Information available at <a href="https://www.trec.texas.gov" target="_blank" rel="noopener noreferrer">www.trec.texas.gov</a></p>
          <p><a href={TREC.iabsUrl} target="_blank" rel="noopener noreferrer" className="font-bold">View the official TREC IABS form (PDF) →</a></p>
        </div>
      </Section>
    </main>
  );
}

export function ConsumerProtectionPage() {
  return (
    <main data-testid="consumer-view">
      <PageHero eyebrow="TREC required disclosure" title="TREC Consumer Protection Notice" />
      <Section>
        <div className="prose prose-slate max-w-none rounded-[2rem] bg-white p-6 shadow-sm md:p-10">
          <p>The Texas Real Estate Commission (TREC) regulates real estate brokers and sales agents, real estate inspectors, home warranty companies, easement and right-of-way agents, and timeshare interest providers.</p>
          <p>You can find more information and check the status of a license holder at <a href="https://www.trec.texas.gov" target="_blank" rel="noopener noreferrer">www.trec.texas.gov</a>.</p>
          <h2>File a complaint</h2>
          <p>If you believe your rights have been violated, file a complaint with TREC at <a href="https://www.trec.texas.gov/complaints" target="_blank" rel="noopener noreferrer">www.trec.texas.gov/complaints</a>.</p>
          <p>TREC may be contacted at:</p>
          <p>P.O. Box 12188<br />Austin, TX 78711-2188<br />Phone: 512-936-3000</p>
          <p><a href={TREC.consumerProtectionUrl} target="_blank" rel="noopener noreferrer" className="font-bold">View the official TREC Consumer Protection Notice (PDF) →</a></p>
        </div>
      </Section>
    </main>
  );
}

// ---------- Footer (global) ----------
export function NewsletterSignup({ compact = false }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [status, setStatus] = useState(""); // "", "loading", "success", "error"
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email || !email.includes("@")) {
      setStatus("error");
      setMessage("Enter a valid email.");
      return;
    }
    setStatus("loading");
    setMessage("");
    try {
      await axios.post(`${API}/subscribers`, {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        city: city.trim(),
        source: "Footer Newsletter",
      });
      setStatus("success");
      setMessage("You're in. The next DFW market update will land in your inbox.");
      setName("");
      setEmail("");
      setCity("");
    } catch (err) {
      setStatus("error");
      setMessage(err?.response?.data?.detail || "Couldn't sign you up. Try again in a moment.");
    }
  };

  return (
    <form onSubmit={submit} data-testid="newsletter-signup" className={`${compact ? "" : "rounded-2xl bg-white/5 p-5 ring-1 ring-white/10"}`}>
      <div className="text-sm font-bold uppercase tracking-wider text-[#C4A56B]">Weekly DFW Market Report</div>
      <p className="mt-2 text-xs leading-5 text-[#E0D2A8]/70">
        Median price, $/sqft, days on market, and one neighborhood story. every week. No spam, unsubscribe anytime.
      </p>
      <div className={`mt-4 grid gap-2 ${compact ? "md:grid-cols-3" : ""}`}>
        <input
          data-testid="newsletter-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name (optional)"
          className="w-full rounded-lg border border-[#C4A56B]/25 bg-[#0a0a0a] px-3 py-2 text-sm text-[#E0D2A8] placeholder:text-[#E0D2A8]/40 focus:border-[#C4A56B] focus:outline-none"
        />
        <input
          data-testid="newsletter-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          className="w-full rounded-lg border border-[#C4A56B]/25 bg-[#0a0a0a] px-3 py-2 text-sm text-[#E0D2A8] placeholder:text-[#E0D2A8]/40 focus:border-[#C4A56B] focus:outline-none"
        />
        <select
          data-testid="newsletter-city"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          className="w-full rounded-lg border border-[#C4A56B]/25 bg-[#0a0a0a] px-3 py-2 text-sm text-[#E0D2A8] focus:border-[#C4A56B] focus:outline-none"
        >
          <option value="">DFW (all cities)</option>
          {CITIES.map((c) => (
            <option key={c.slug} value={c.name}>{c.name}</option>
          ))}
        </select>
      </div>
      <button
        type="submit"
        data-testid="newsletter-submit"
        disabled={status === "loading"}
        className="mt-3 w-full rounded-lg bg-[#C4A56B] px-4 py-2.5 text-sm font-bold text-[#1E1E1E] transition hover:bg-[#A68B51] disabled:opacity-60 md:w-auto md:px-6"
      >
        {status === "loading" ? "Subscribing…" : "Get the Weekly Report"}
      </button>
      {message && (
        <div
          data-testid={`newsletter-${status}`}
          className={`mt-3 text-xs ${status === "success" ? "text-emerald-300" : "text-red-300"}`}
        >
          {message}
        </div>
      )}
    </form>
  );
}

export function SiteFooter({ navigate }) {
  return (
    <footer data-testid="site-footer" className="mt-16 bg-[#0a0a0a] text-[#E0D2A8]/80">
      <div className="mx-auto max-w-6xl px-4 py-12 md:px-8">
        <div className="mb-10 rounded-2xl border border-[#C4A56B]/25 bg-[#141414] p-6">
          <NewsletterSignup compact />
        </div>
        <div className="grid gap-10 md:grid-cols-4">
          <div>
            <img src={BRAND.logo} alt={BRAND.name} className="h-14 w-auto object-contain" />
            <div className="mt-2 text-[10px] uppercase tracking-[0.22em] text-[#C4A56B]">{BRAND.tagline}</div>
            <p className="mt-4 text-sm leading-6">DFW real estate concierge. Buy, sell, or rent-to-buy with Torey Irving and Jason, your personal guide.</p>
            <div className="mt-4 inline-flex items-center gap-2 rounded-lg border border-[#C4A56B]/25 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-[#E0D2A8]">
              <span className="rounded bg-[#E0D2A8] px-1.5 py-0.5 text-[8px] text-black">EHO</span> Equal Housing Opportunity
            </div>
          </div>

          <div>
            <div className="text-sm font-bold uppercase tracking-wider text-[#C4A56B]">Explore</div>
            <ul className="mt-3 space-y-2 text-sm">
              <li><button onClick={() => navigate("listings")} className="hover:text-[#C4A56B]">DFW Listings</button></li>
              <li><button onClick={() => navigate("blog")} className="hover:text-[#C4A56B]">Market Insights</button></li>
              <li><button onClick={() => navigate("mortgage")} className="hover:text-[#C4A56B]">Mortgage Calculator</button></li>
              <li><button onClick={() => navigate("assistant")} className="hover:text-[#C4A56B]">Ask Jason</button></li>
            </ul>
          </div>

          <div>
            <div className="text-sm font-bold uppercase tracking-wider text-[#C4A56B]">Company</div>
            <ul className="mt-3 space-y-2 text-sm">
              <li><button onClick={() => navigate("about")} className="hover:text-[#C4A56B]">About Torey</button></li>
              <li><button onClick={() => navigate("why-us")} className="hover:text-[#C4A56B]">Why Choose Us</button></li>
              <li><button onClick={() => navigate("testimonials")} className="hover:text-[#C4A56B]">Client Reviews</button></li>
              <li><button onClick={() => navigate("faq")} className="hover:text-[#C4A56B]">FAQ</button></li>
              <li><button data-testid="footer-for-agents-link" onClick={() => navigate("for-agents")} className="hover:text-[#C4A56B]">For Agents</button></li>
              <li><button onClick={() => navigate("contact")} className="hover:text-[#C4A56B]">Contact</button></li>
            </ul>
          </div>

          <div>
            <div className="text-sm font-bold uppercase tracking-wider text-[#C4A56B]">Office</div>
            <div className="mt-3 text-sm leading-6">
              <div>{BROKERAGE.office.full}</div>
              <a href={`tel:${BROKERAGE.phone}`} className="mt-1 block hover:text-[#C4A56B]">{BROKERAGE.phone}</a>
              <a href={`mailto:${BROKERAGE.email}`} className="block hover:text-[#C4A56B]">{BROKERAGE.email}</a>
              <div className="mt-2 text-xs text-[#E0D2A8]/60">{BROKERAGE.hours}</div>
            </div>
          </div>
        </div>

        <div className="mt-10 border-t border-[#C4A56B]/20 pt-6 text-xs leading-6 text-[#E0D2A8]/60">
          <div className="grid gap-2 md:flex md:flex-wrap md:items-center md:gap-x-6 md:gap-y-2">
            <div>© {new Date().getFullYear()} {BROKERAGE.name}. All rights reserved.</div>
            <div>TX Broker License #{BROKERAGE.licenseNumber}</div>
            <div>{AGENT.name} · TX Realtor License #{AGENT.licenseNumber}</div>
          </div>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
            <button onClick={() => navigate("privacy")} className="hover:text-[#C4A56B]">Privacy Policy</button>
            <button onClick={() => navigate("terms")} className="hover:text-[#C4A56B]">Terms of Service</button>
            <button data-testid="footer-sms-terms" onClick={() => navigate("sms-terms")} className="hover:text-[#C4A56B]">SMS Terms</button>
            <button data-testid="footer-unsubscribe" onClick={() => navigate("unsubscribe")} className="hover:text-[#C4A56B]">Unsubscribe (SMS)</button>
            <button onClick={() => navigate("fair-housing")} className="hover:text-[#C4A56B]">Fair Housing</button>
            <button onClick={() => navigate("iabs")} className="hover:text-[#C4A56B]">TREC IABS</button>
            <button onClick={() => navigate("consumer-protection")} className="hover:text-[#C4A56B]">TREC Consumer Notice</button>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ---------- For Agents (recruitment + build landing page) ----------
export function ForAgentsPage({ navigate }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", years: "", gci: "", why: "", track: "recruit", smsConsent: false });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      const trackLabel = form.track === "build" ? "PLATFORM BUILD" : "BROKERAGE RECRUIT";
      const src = form.track === "build" ? "Platform Build Inquiry" : "Agent Recruit";
      const res = await axios.post(`${API}/leads`, {
        type: "Buyer", // pipeline-compatible; we tag it via source + concern below
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        source: src,
        concern: `[AGENT RECRUIT] Track: ${trackLabel} · Years licensed: ${form.years || "—"} · Last-12 GCI: ${form.gci || "—"} · Why: ${form.why || "—"}`,
        smsConsent: !!form.smsConsent,
        smsConsentText: form.smsConsent ? SMS_CONSENT_TEXT : "",
        smsConsentSource: "for_agents_form",
        hp: "",
      });
      if (res?.data?.id) {
        setSent(true);
      } else {
        setError("Received an unexpected response. Please try again or text 817-308-0922.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Something went wrong.";
      setError(`Couldn't send: ${msg}. Please try again or text 817-308-0922.`);
    } finally {
      setSubmitting(false);
    }
  }

  function scrollToBook(track) {
    setForm((f) => ({ ...f, track }));
    setTimeout(() => {
      const el = document.getElementById("book");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 50);
  }

  const stats = [
    { k: "5-min", v: "Hot-lead alert to your phone with a call script written for you" },
    { k: "17 stages", v: "Kanban pipeline from first touch to referral request after close" },
    { k: "0 clicks", v: "Home-value PDFs auto-email themselves the moment a seller lands" },
    { k: "2-4 wks", v: "Delivery timeline for your own private white-label build" },
  ];

  const pains = [
    { p: "Paying $200-$500/mo for Follow Up Boss, kvCORE, Chime, or BoomTown", f: "Included. Every dollar you save is a dollar toward marketing." },
    { p: "A Placester or Sierra site that doesn't actually capture leads into your CRM", f: "Direct Placester → CRM bridge already built. Every inquiry lands on your Kanban, tagged, scored, ready to work." },
    { p: "Sellers ghost you because your CMA takes 3 days to produce", f: "The moment a seller fills the value form, a branded PDF hits their inbox in 15 seconds." },
    { p: "Cold leads pile up because you don't know who to call first", f: "Jason (Claude Sonnet 4.5) scores every lead and tells you the next best action." },
    { p: "You lose deals in the option period from missed inspection deadlines", f: "8-stage concierge tracker with auto-calculated dates and 'at-risk' alerts before you drop the ball." },
    { p: "Splitting 70/30 with your brokerage while paying full tech tab yourself", f: "Better splits with Irving REG plus a tech stack that would cost you $500+/mo à la carte." },
  ];

  const shots = [
    { title: "Today's Follow-Ups", body: "Open your dashboard at 8am. Overdue, due today, hot leads with no touch in 24h — all in one screen. Clear the red row first, then the gold." },
    { title: "AI Hot Lead Alerts", body: "Jason (Claude) monitors every lead. When someone crosses the threshold (mortgage-ready + tight timeline + high engagement), your phone lights up with a 2-sentence call script." },
    { title: "17-Stage Kanban", body: "Every lead is a card. Drag from New → Contacted → Consult Booked → Showing Complete → Under Contract → Closed → Referral Given. The board is your day." },
    { title: "8-Stage Concierge Tracker", body: "Every executed contract becomes a transaction card. Auto-calculated option period end, target close date. Cards turn red when something slips." },
    { title: "Home Value PDF Auto-Send", body: "Seller submits your value form → branded PDF hits their inbox in 15 seconds → they think you're magic." },
    { title: "Zapier + Direct Integrations", body: "Placester, SendGrid, Twilio, Zapier. Your existing stack talks to the CRM. No CSVs, no manual entry, no dropped leads." },
  ];

  return (
    <main className="bg-[#0F0F0F] text-[#E0D2A8]" data-testid="for-agents-view">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(196,165,107,0.15),transparent_60%)]" />
        <div className="relative mx-auto max-w-6xl px-4 py-16 md:px-8 md:py-24">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#C4A56B]">For solo agents</div>
          <h1 className="mt-4 font-serif text-4xl leading-tight md:text-6xl">
            Stop paying $500/mo for tech<br /><span className="text-[#C4A56B]">that doesn't talk to itself.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-[#E0D2A8]/80">
            Two ways to get the platform you're looking at right now — an AI-scored 17-stage CRM,
            luxury brokerage website, auto-generated home value PDFs, and a concierge tracker that
            stops you from dropping deals between contract and close.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button data-testid="hero-cta-recruit" onClick={() => scrollToBook("recruit")} className="rounded-full bg-[#C4A56B] px-6 py-3 font-bold text-[#1E1E1E] hover:bg-[#d4b876]">
              Join Irving REG (free tech) →
            </button>
            <button data-testid="hero-cta-build" onClick={() => scrollToBook("build")} className="rounded-full border border-[#C4A56B] px-6 py-3 font-bold text-[#C4A56B] hover:bg-[#C4A56B]/10">
              Buy your own build →
            </button>
          </div>
          <div className="mt-10 grid gap-3 sm:grid-cols-2 md:grid-cols-4">
            {stats.map((s) => (
              <div key={s.k} className="rounded-2xl border border-[#C4A56B]/25 bg-[#141414] p-4">
                <div className="font-serif text-2xl text-[#C4A56B]">{s.k}</div>
                <div className="mt-1 text-xs text-[#E0D2A8]/70">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Two Paths — side-by-side */}
      <section id="paths" className="border-t border-[#C4A56B]/15 bg-[#0B0B0B]">
        <div className="mx-auto max-w-6xl px-4 py-16 md:px-8">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#C4A56B]">Two paths</div>
          <h2 className="mt-3 font-serif text-3xl md:text-4xl">Pick the one that fits.</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {/* Path A — Join IREG */}
            <div className="rounded-3xl border-2 border-[#C4A56B] bg-gradient-to-br from-[#1a1a1a] to-[#141414] p-7">
              <div className="inline-flex rounded-full bg-[#C4A56B] px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[#1E1E1E]">Path A · Best for career move</div>
              <h3 className="mt-4 font-serif text-2xl text-[#E0D2A8]">Join Irving Real Estate Group</h3>
              <p className="mt-2 text-sm text-[#E0D2A8]/75">Hang your license with us. Get the entire platform included. Better splits than the industry average. Zero tech bill.</p>
              <div className="mt-6 flex items-baseline gap-2">
                <div className="font-serif text-5xl text-[#C4A56B]">$0</div>
                <div className="text-sm text-[#E0D2A8]/70">/month tech spend</div>
              </div>
              <ul className="mt-5 space-y-2 text-sm text-[#E0D2A8]/85">
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> Full platform included (retail: $500+/mo)</li>
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> Better commission splits than industry avg</li>
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> Your agent profile on irvingrealestategroup.com</li>
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> Broker mentorship + deal review from Torey</li>
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> Access to house leads flowing through the site</li>
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> All future platform upgrades automatic</li>
              </ul>
              <button data-testid="path-recruit-cta" onClick={() => scrollToBook("recruit")} className="mt-6 w-full rounded-full bg-[#C4A56B] px-6 py-3 font-bold text-[#1E1E1E] hover:bg-[#d4b876]">
                Book a licensing conversation →
              </button>
            </div>
            {/* Path B — Build */}
            <div className="rounded-3xl border border-[#C4A56B]/30 bg-[#141414] p-7">
              <div className="inline-flex rounded-full border border-[#C4A56B]/50 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[#C4A56B]">Path B · Best for staying independent</div>
              <h3 className="mt-4 font-serif text-2xl text-[#E0D2A8]">Buy your own build</h3>
              <p className="mt-2 text-sm text-[#E0D2A8]/75">Prefer to keep your brokerage? We build you your own private instance of the platform under your brand.</p>
              <div className="mt-6 flex items-baseline gap-2">
                <div className="font-serif text-4xl text-[#C4A56B]">from $2,495</div>
                <div className="text-sm text-[#E0D2A8]/70">setup + $149/mo</div>
              </div>
              <ul className="mt-5 space-y-2 text-sm text-[#E0D2A8]/85">
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> Your brand, your domain, your leads</li>
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> Same platform running under Irving REG</li>
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> Full source data ownership (your CRM stays yours)</li>
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> Three tiers, pick what fits (see below)</li>
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> Delivered live in 2-4 weeks</li>
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> Ongoing maintenance + upgrades included</li>
              </ul>
              <button data-testid="path-build-cta" onClick={() => scrollToBook("build")} className="mt-6 w-full rounded-full border-2 border-[#C4A56B] px-6 py-3 font-bold text-[#C4A56B] hover:bg-[#C4A56B]/10">
                See pricing tiers →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Tiers (build track) */}
      <section id="pricing" className="border-t border-[#C4A56B]/15">
        <div className="mx-auto max-w-6xl px-4 py-16 md:px-8">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#C4A56B]">Build pricing</div>
          <h2 className="mt-3 font-serif text-3xl md:text-4xl">Three tiers. Pick what fits your book.</h2>
          <p className="mt-3 max-w-2xl text-sm text-[#E0D2A8]/70">Delivered under your brand, your domain. No lock-in — cancel anytime after month 3 (setup fee is non-refundable but the platform stays yours).</p>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {/* Starter */}
            <div className="flex flex-col rounded-3xl border border-[#C4A56B]/25 bg-[#141414] p-6" data-testid="pricing-starter">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#C4A56B]">Starter Build</div>
              <div className="mt-1 text-xs text-[#E0D2A8]/60">For solo agents under $2M GCI</div>
              <div className="mt-5">
                <span className="font-serif text-4xl text-[#E0D2A8]">$2,495</span>
                <span className="text-sm text-[#E0D2A8]/60"> setup</span>
              </div>
              <div className="text-sm text-[#E0D2A8]/70">+ $149/month</div>
              <ul className="mt-6 flex-1 space-y-2 text-sm text-[#E0D2A8]/85">
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> Dark-luxury brokerage website</li>
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> Full CRM + 17-stage Kanban</li>
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> AI hot-lead scoring (Claude 4.5)</li>
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> Placester / Zapier lead bridge</li>
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> Custom domain + SSL</li>
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> SendGrid outbound email</li>
                <li className="flex gap-2 text-[#E0D2A8]/40"><span>—</span> Home Value PDF auto-send</li>
                <li className="flex gap-2 text-[#E0D2A8]/40"><span>—</span> Concierge tracker</li>
              </ul>
              <button data-testid="pricing-starter-cta" onClick={() => scrollToBook("build")} className="mt-6 rounded-full border border-[#C4A56B]/50 px-4 py-3 text-sm font-bold text-[#C4A56B] hover:bg-[#C4A56B]/10">
                Get started →
              </button>
            </div>
            {/* Pro — highlighted */}
            <div className="flex flex-col rounded-3xl border-2 border-[#C4A56B] bg-gradient-to-br from-[#1a1a1a] to-[#141414] p-6 shadow-2xl md:-translate-y-3" data-testid="pricing-pro">
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#C4A56B]">Pro Build</div>
                <span className="rounded-full bg-[#C4A56B] px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-[#1E1E1E]">Most Popular</span>
              </div>
              <div className="mt-1 text-xs text-[#E0D2A8]/60">For solo agents $2-5M GCI · brand-conscious</div>
              <div className="mt-5">
                <span className="font-serif text-4xl text-[#E0D2A8]">$4,995</span>
                <span className="text-sm text-[#E0D2A8]/60"> setup</span>
              </div>
              <div className="text-sm text-[#E0D2A8]/70">+ $299/month</div>
              <ul className="mt-6 flex-1 space-y-2 text-sm text-[#E0D2A8]">
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> <strong>Everything in Starter</strong></li>
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> Home Value PDF auto-send</li>
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> 8-stage Concierge Tracker</li>
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> AI-generated call scripts per lead</li>
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> Content Studio (blog + market reports)</li>
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> Full brand customization</li>
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> Twilio SMS integration ready</li>
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> Priority support · 24h response</li>
              </ul>
              <button data-testid="pricing-pro-cta" onClick={() => scrollToBook("build")} className="mt-6 rounded-full bg-[#C4A56B] px-4 py-3 text-sm font-bold text-[#1E1E1E] hover:bg-[#d4b876]">
                Book a build call →
              </button>
            </div>
            {/* Team */}
            <div className="flex flex-col rounded-3xl border border-[#C4A56B]/25 bg-[#141414] p-6" data-testid="pricing-team">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#C4A56B]">Team / White-label</div>
              <div className="mt-1 text-xs text-[#E0D2A8]/60">For 2-5 agent teams or boutique brokerages</div>
              <div className="mt-5">
                <span className="font-serif text-4xl text-[#E0D2A8]">$9,995+</span>
                <span className="text-sm text-[#E0D2A8]/60"> setup</span>
              </div>
              <div className="text-sm text-[#E0D2A8]/70">+ $599+/month · custom quote</div>
              <ul className="mt-6 flex-1 space-y-2 text-sm text-[#E0D2A8]/85">
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> <strong>Everything in Pro</strong></li>
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> Multi-agent seats + role permissions</li>
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> Team reporting + leaderboards</li>
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> Custom integrations (MLS direct feed, etc.)</li>
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> Fully custom brand from scratch</li>
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> Dedicated support with SLA</li>
                <li className="flex gap-2"><span className="text-[#C4A56B]">✓</span> Onboarding + training for entire team</li>
              </ul>
              <button data-testid="pricing-team-cta" onClick={() => scrollToBook("build")} className="mt-6 rounded-full border border-[#C4A56B]/50 px-4 py-3 text-sm font-bold text-[#C4A56B] hover:bg-[#C4A56B]/10">
                Request a quote →
              </button>
            </div>
          </div>
          <p className="mt-8 text-center text-[11px] text-[#E0D2A8]/50">All tiers include hosting, monthly platform updates, LLM API costs, security patching, and email support. Cancel anytime after month 3 (60-day notice for Team tier).</p>
        </div>
      </section>

      {/* Problem / Solution */}
      <section className="border-t border-[#C4A56B]/15">
        <div className="mx-auto max-w-6xl px-4 py-16 md:px-8">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#C4A56B]">The tax on solo agents</div>
          <h2 className="mt-3 font-serif text-3xl md:text-4xl">You already know it.</h2>
          <p className="mt-4 max-w-3xl text-[#E0D2A8]/75">
            Solo agents write bigger checks per month for less software than anyone else in this business.
            Here's what stops when you switch:
          </p>
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {pains.map((p) => (
              <div key={p.p} className="rounded-3xl border border-[#C4A56B]/20 bg-[#141414] p-5">
                <div className="text-xs font-bold uppercase tracking-widest text-red-300/80">Before</div>
                <div className="mt-1 text-sm text-[#E0D2A8]/85">{p.p}</div>
                <div className="mt-4 text-xs font-bold uppercase tracking-widest text-[#C4A56B]">After</div>
                <div className="mt-1 text-sm text-[#E0D2A8]">{p.f}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Stack */}
      <section id="stack" className="border-t border-[#C4A56B]/15 bg-[#0B0B0B]">
        <div className="mx-auto max-w-6xl px-4 py-16 md:px-8">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#C4A56B]">The stack you get on day one</div>
          <h2 className="mt-3 font-serif text-3xl md:text-4xl">Six pieces of tech.<br />Each one usually costs $50-$150/mo elsewhere.</h2>
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {shots.map((s, idx) => (
              <div key={s.title} className="rounded-3xl border border-[#C4A56B]/20 bg-[#141414] p-6">
                <div className="font-serif text-4xl text-[#C4A56B]/40">{String(idx + 1).padStart(2, "0")}</div>
                <div className="mt-2 text-lg font-bold text-[#E0D2A8]">{s.title}</div>
                <div className="mt-3 text-sm text-[#E0D2A8]/75">{s.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Torey's Story */}
      <section className="border-t border-[#C4A56B]/15">
        <div className="mx-auto max-w-4xl px-4 py-16 text-center md:px-8">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#C4A56B]">Why I built this</div>
          <h2 className="mt-3 font-serif text-3xl md:text-4xl">"I got tired of paying five vendors to do one job badly."</h2>
          <p className="mt-6 text-lg text-[#E0D2A8]/80">
            I'm {AGENT.name} — {AGENT.title}, licensed since {AGENT.yearsLicensed?.replace(/\D/g, "") || "2008"}.
            After 15+ years watching solo agents drown in disconnected tools, I built the CRM I always wanted:
            one dashboard that tells me who to call, when to call, and what to say. Then I got greedy — I asked
            my broker split to be better than the industry average, too. Now I want other solo agents to have both.
          </p>
          <div className="mt-8 inline-flex items-center gap-3 rounded-full border border-[#C4A56B]/30 bg-[#141414] px-5 py-3">
            <img src={AGENT.photo} alt={AGENT.name} className="h-12 w-12 rounded-full object-cover object-top" />
            <div className="text-left">
              <div className="font-serif text-[#C4A56B]">{AGENT.name}</div>
              <div className="text-xs text-[#E0D2A8]/70">Broker of Record · Irving Real Estate Group</div>
            </div>
          </div>
        </div>
      </section>

      {/* Book a demo form */}
      <section id="book" className="border-t border-[#C4A56B]/15 bg-[#0B0B0B]">
        <div className="mx-auto max-w-3xl px-4 py-16 md:px-8">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#C4A56B]">Book a private conversation</div>
          <h2 className="mt-3 font-serif text-3xl md:text-4xl">15 minutes. Real screens. Your questions.</h2>
          <p className="mt-4 text-[#E0D2A8]/75">
            Tell me a little about where you are today. I'll come to the call with a walk-through built around
            your book of business, your current tech spend, and what would need to be true for {form.track === "build" ? "a build to make sense" : "a move to make sense"}. If it's not a fit, I'll say so and point you somewhere better.
          </p>
          {sent ? (
            <div data-testid="for-agents-submitted" className="mt-10 rounded-3xl border border-[#C4A56B] bg-[#141414] p-8 text-center">
              <div className="font-serif text-2xl text-[#C4A56B]">You're in.</div>
              <p className="mt-3 text-[#E0D2A8]/80">
                Torey will personally reach out within 24 hours to lock a time. In the meantime, poke around
                the site — you're literally standing inside the platform.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-10 space-y-4 rounded-3xl border border-[#C4A56B]/25 bg-[#141414] p-6">
              {/* Track selector */}
              <div>
                <div className="mb-2 text-[11px] font-bold uppercase tracking-widest text-[#C4A56B]">What are you interested in?</div>
                <div className="flex rounded-full bg-[#0F0F0F] p-1 text-xs font-bold">
                  <button
                    type="button"
                    data-testid="track-recruit"
                    onClick={() => setForm({ ...form, track: "recruit" })}
                    className={`flex-1 rounded-full px-4 py-2 transition ${form.track === "recruit" ? "bg-[#C4A56B] text-[#1E1E1E]" : "text-[#E0D2A8]/70"}`}
                  >
                    Join Irving REG
                  </button>
                  <button
                    type="button"
                    data-testid="track-build"
                    onClick={() => setForm({ ...form, track: "build" })}
                    className={`flex-1 rounded-full px-4 py-2 transition ${form.track === "build" ? "bg-[#C4A56B] text-[#1E1E1E]" : "text-[#E0D2A8]/70"}`}
                  >
                    Buy my own build
                  </button>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <label className="text-xs">
                  <span className="mb-1 block font-bold uppercase tracking-wide text-[#C4A56B]">Full Name *</span>
                  <input data-testid="for-agents-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl border border-[#C4A56B]/25 bg-[#0F0F0F] px-4 py-3 text-sm text-[#E0D2A8]" />
                </label>
                <label className="text-xs">
                  <span className="mb-1 block font-bold uppercase tracking-wide text-[#C4A56B]">Email *</span>
                  <input data-testid="for-agents-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-xl border border-[#C4A56B]/25 bg-[#0F0F0F] px-4 py-3 text-sm text-[#E0D2A8]" />
                </label>
                <label className="text-xs">
                  <span className="mb-1 block font-bold uppercase tracking-wide text-[#C4A56B]">Phone</span>
                  <input data-testid="for-agents-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full rounded-xl border border-[#C4A56B]/25 bg-[#0F0F0F] px-4 py-3 text-sm text-[#E0D2A8]" />
                </label>
                <label className="text-xs">
                  <span className="mb-1 block font-bold uppercase tracking-wide text-[#C4A56B]">Years Licensed</span>
                  <select data-testid="for-agents-years" value={form.years} onChange={(e) => setForm({ ...form, years: e.target.value })} className="w-full rounded-xl border border-[#C4A56B]/25 bg-[#0F0F0F] px-4 py-3 text-sm text-[#E0D2A8]">
                    <option value="">Select…</option>
                    <option>Under 1 year</option>
                    <option>1–3 years</option>
                    <option>3–7 years</option>
                    <option>7+ years</option>
                  </select>
                </label>
                <label className="md:col-span-2 text-xs">
                  <span className="mb-1 block font-bold uppercase tracking-wide text-[#C4A56B]">Last-12-Month GCI</span>
                  <select data-testid="for-agents-gci" value={form.gci} onChange={(e) => setForm({ ...form, gci: e.target.value })} className="w-full rounded-xl border border-[#C4A56B]/25 bg-[#0F0F0F] px-4 py-3 text-sm text-[#E0D2A8]">
                    <option value="">Select…</option>
                    <option>Under $50k</option>
                    <option>$50k–$150k</option>
                    <option>$150k–$300k</option>
                    <option>$300k+</option>
                  </select>
                </label>
                <label className="md:col-span-2 text-xs">
                  <span className="mb-1 block font-bold uppercase tracking-wide text-[#C4A56B]">What's the #1 thing that would have to be true?</span>
                  <textarea data-testid="for-agents-why" value={form.why} onChange={(e) => setForm({ ...form, why: e.target.value })} rows={3} placeholder="Better splits, real CRM included, no CAP fee, luxury brand, etc." className="w-full rounded-xl border border-[#C4A56B]/25 bg-[#0F0F0F] px-4 py-3 text-sm text-[#E0D2A8]" />
                </label>
              </div>
              <SmsConsentCheckbox theme="dark" checked={form.smsConsent} onChange={(v) => setForm({ ...form, smsConsent: v })} navigate={navigate} />
              <button data-testid="for-agents-submit" type="submit" disabled={submitting} className="w-full rounded-full bg-[#C4A56B] px-6 py-4 font-bold text-[#1E1E1E] hover:bg-[#d4b876] disabled:opacity-60">
                {submitting ? "Sending…" : (form.track === "build" ? "Request a build call →" : "Request a licensing call →")}
              </button>
              {error && (
                <div data-testid="for-agents-error" className="rounded-xl border border-red-500/40 bg-red-950/40 px-4 py-3 text-sm text-red-200">
                  {error}
                </div>
              )}
              <p className="text-center text-[11px] text-[#E0D2A8]/50">Your details go straight to Torey. No spam list, no drip campaign. Human reply within 24 hours.</p>
            </form>
          )}
        </div>
      </section>

      {/* Footer disclosure */}
      <div className="border-t border-[#C4A56B]/10 bg-[#0F0F0F] py-6 text-center text-[10px] uppercase tracking-widest text-[#E0D2A8]/40">
        Irving Real Estate Group · Broker License #0593548 · Equal Housing Opportunity
      </div>
    </main>
  );
}

